import { ChevronRight, Layers, MessageSquare, Plus } from "lucide-react"
import * as React from "react"

import { Skeleton } from "@/components/ui/alert"
import { useToast } from "@/components/ui/toast"
import { Button } from "@/components/ui/button"
import {
  DataTable,
  Pagination,
  type Column,
  type RowKey,
  type SortState,
} from "@/components/ui/data-table"
import { EmptyState } from "@/components/ui/empty-state"
import { Kbd, StatCard } from "@/components/ui/stat-card"
import { ActiveFilterChips } from "@/components/ui/table-filters"
import { type SortOption } from "@/components/ui/table-sort"
import { TableToolbar } from "@/components/ui/table-toolbar"
import { formatRelative } from "@/lib/format-date"
import { useHasLoadedOnce, useTable, type TableSort } from "@/lib/table"
import { useSlashShortcut } from "@/lib/use-slash-shortcut"
import { cn } from "@/lib/utils"

import { type DateStr } from "@/components/ui/date-picker"
import { type AcceptedFile, type DropzoneStatus } from "@/components/ui/file-dropzone"
import { NewReviewSheet, type ResolveState } from "@/features/intake"

import { bannerFor } from "@/lib/api-error-message"
import { COMING_SOON, FEATURES } from "@/lib/features"

import { useQueryClient } from "@tanstack/react-query"

import { threadsQuery, useQueueTotalsQuery } from "./queue-api"
import {
  STATUS_LABEL,
  STATUS_TONE,
  filterQueue,
  type QueueRow,
  type QueueStatus,
} from "./queue-data"
import { buildQueueFilterSchema, filtersToQuery } from "./queue-filters"

const SORT_OPTIONS: SortOption[] = [
  { id: "updatedAt", label: "Last updated", ascLabel: "Oldest first", descLabel: "Newest first" },
  { id: "product", label: "Product name", ascLabel: "A → Z", descLabel: "Z → A" },
  { id: "findings", label: "Findings count", ascLabel: "Fewest first", descLabel: "Most first" },
  { id: "versions", label: "Version count", ascLabel: "Fewest first", descLabel: "Most first" },
]

const STATUS_BADGE: Record<QueueStatus, string> = {
  queued: "bg-info-tint text-info ring-info/20",
  in_review: "bg-print-tint text-print ring-print/20",
  needs_sme: "bg-people-tint text-people ring-people/20",
  cleared: "bg-master-tint text-master ring-master/20",
}

const STATUS_DOT: Record<QueueStatus, string> = {
  queued: "bg-info",
  in_review: "bg-print",
  needs_sme: "bg-people",
  cleared: "bg-master",
}

const EMPTY_COUNTS: Record<QueueStatus, number> = {
  queued: 0,
  in_review: 0,
  needs_sme: 0,
  cleared: 0,
}

export function QueuePage(): JSX.Element {
  // Schema depends on the loaded rows because assignee + submitter dropdown
  // options are derived from them. Empty on first render; refills when the
  // /threads query resolves.
  const { toast } = useToast()
  const searchRef = React.useRef<HTMLInputElement>(null)
  const [expanded, setExpanded] = React.useState<Set<RowKey>>(new Set())
  const [intakeOpen, setIntakeOpen] = React.useState(false)

  useSlashShortcut(searchRef)

  const qc = useQueryClient()
  const fetchData = React.useCallback(
    async (params: {
      page: number
      pageSize: number
      sort: TableSort | null
      filters: Record<string, unknown>
      search: string
    }) => {
      const page = await qc.fetchQuery(
        threadsQuery({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          sort: params.sort,
          includeArchived: true,
        }),
      )
      return { rows: page.items, total: page.total }
    },
    [qc],
  )

  const { state, actions } = useTable<QueueRow>({
    fetchData,
    defaultSort: { field: "updatedAt", dir: "desc" },
    storageKey: "reviews",
  })

  // Client-side filters (assignee/submitter/etc.) still apply within the current
  // page only. Server-side status/assignee filters are backend #22 territory.
  const filteredRows = React.useMemo(() => {
    const qf = filtersToQuery(state.filters as Parameters<typeof filtersToQuery>[0])
    return filterQueue(state.rows, qf)
  }, [state.rows, state.filters])

  const schema = React.useMemo(() => buildQueueFilterSchema(state.rows), [state.rows])

  const { data: totals, error: loadError } = useQueueTotalsQuery()
  const totalCounts = totals?.counts ?? EMPTY_COUNTS

  // "First paint" while the initial fetch is in flight. Filter/sort
  // roundtrips after that don't re-shimmer the stat cards.
  const hasLoadedOnce = useHasLoadedOnce(state.isLoading)

  const activeStatus = (state.filters["status"] as QueueStatus | undefined) ?? null

  const toggleStatus = React.useCallback(
    (s: QueueStatus) => actions.setFilter("status", activeStatus === s ? null : s),
    [activeStatus, actions],
  )

  const toggleExpand = React.useCallback((key: RowKey) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }, [])

  const setSort = React.useCallback(
    (s: SortState) => actions.setSort(s ? { field: s.columnId, dir: s.dir } : null),
    [actions],
  )

  const sortState: SortState = state.sort
    ? { columnId: state.sort.field, dir: state.sort.dir }
    : null

  const columns = React.useMemo<Array<Column<QueueRow>>>(
    () => [
      {
        id: "product",
        header: "Product",
        width: "minmax(0,1.6fr)",
        cell: (row) => <ProductCell row={row} />,
      },
      {
        id: "status",
        header: "Status",
        width: "132px",
        priority: 2,
        cell: (row) => <StatusPill status={row.status} />,
      },
      {
        id: "findings",
        header: "Findings",
        width: "minmax(0,1fr)",
        priority: 2,
        cell: (row) => <FindingsCell row={row} />,
      },
      {
        id: "owner",
        header: "Assignee",
        width: "132px",
        priority: 3,
        cell: (row) => <span className="truncate text-body-sm text-text-2">{row.owner}</span>,
      },
      {
        id: "comms",
        header: "Comms",
        width: "96px",
        align: "end",
        priority: 3,
        cell: (row) => <CommsCell unread={row.commsUnread} />,
      },
      {
        id: "updatedAt",
        header: "Updated",
        width: "104px",
        align: "end",
        cell: (row) => (
          <span className="font-id tabular-nums text-body-sm text-text-3">
            {formatRelative(row.updatedAt)}
          </span>
        ),
      },
    ],
    [],
  )

  const filtered = state.search.length > 0 || state.filterCount > 0

  return (
    <div className="flex h-full flex-col gap-5 px-6 py-5">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-subtitle text-text">Review queue</h1>
        <Button
          variant="primary"
          leadingIcon={<Plus />}
          onClick={() => {
            if (FEATURES.newReview) setIntakeOpen(true)
            else toast(COMING_SOON)
          }}
        >
          New review
        </Button>
      </header>

      {FEATURES.newReview && (
        <NewReviewContainer open={intakeOpen} onOpenChange={setIntakeOpen} />
      )}

      {loadError && (() => {
        const banner = bannerFor(loadError, "the review queue")
        return (
          <div
            role="alert"
            className="rounded-md border border-people/30 bg-people-tint/40 px-4 py-3 text-body-sm text-people"
          >
            <div className="font-medium">{banner.title}</div>
            <div className="mt-0.5">{banner.body}</div>
          </div>
        )
      })()}

      <section
        aria-label="Queue totals — filter by status"
        className="grid grid-cols-2 gap-2.5 sm:grid-cols-4"
      >
        {(Object.keys(STATUS_LABEL) as QueueStatus[]).map((s) => (
          <StatCard
            key={s}
            value={
              hasLoadedOnce ? (
                // A `~` prefix flags counts that come from a capped snapshot rather than
                // a true aggregate — happens once the queue exceeds the totals-page size.
                // Real fix is backend task #14 (a dedicated /reviews/stats endpoint).
                `${totals?.countsAreApproximate ? "~" : ""}${totalCounts[s] ?? 0}`
              ) : (
                <Skeleton className="h-6 w-10 rounded" />
              )
            }
            label={STATUS_LABEL[s]}
            tone={STATUS_TONE[s]}
            onSelect={() => toggleStatus(s)}
            selected={activeStatus === s}
          />
        ))}
      </section>

      <TableToolbar
        search={state.search}
        onSearchChange={actions.setSearch}
        searchRef={searchRef}
        searchPlaceholder="Search product, EPA reg. no. or submitter"
        searchAriaLabel="Search review queue"
        searchSuffix={state.search.length === 0 ? <Kbd keys="/" /> : null}
        filterSchema={schema}
        filterValues={state.filters}
        onFiltersChange={actions.setFilters}
        onClearAllFilters={actions.clearAllFilters}
        sortOptions={SORT_OPTIONS}
        sort={state.sort}
        onSortChange={actions.setSort}
      />

      <ActiveFilterChips
        schema={schema}
        values={state.filters}
        onRemove={actions.clearFilter}
        onClearAll={actions.clearAllFilters}
      />

      <div className="min-h-0 flex-1">
        <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-surface">
          <div className="min-h-0 flex-1 overflow-auto">
            <DataTable<QueueRow>
              label="Review queue"
              columns={columns}
              rows={filteredRows}
              rowKey={(r) => r.id}
              sort={sortState}
              onSortChange={setSort}
              expandedKeys={expanded}
              onToggleExpand={toggleExpand}
              renderExpanded={(row) => <ExpandedThread row={row} />}
              stickyHeader
              loading={state.isLoading}
              className="rounded-none border-0"
              empty={
                <EmptyState
                  bare
                  reason={filtered ? "filtered" : "empty"}
                  title={filtered ? "Nothing matches" : "The queue is empty"}
                  body={
                    filtered
                      ? "Try a different term, or clear the search and filters to see the whole queue."
                      : "New submissions land here as they arrive."
                  }
                  action={
                    filtered ? (
                      <Button
                        size="sm"
                        onClick={() => {
                          actions.setSearch("")
                          actions.clearAllFilters()
                        }}
                      >
                        Clear filters
                      </Button>
                    ) : null
                  }
                />
              }
            />
          </div>
          <div className="border-t border-border">
            <Pagination
              page={state.page}
              pageSize={state.pageSize}
              total={state.total}
              onPageChange={actions.setPage}
              onPageSizeChange={actions.setPageSize}
              pageSizes={[10, 25, 50]}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function StatusPill({ status }: { status: QueueStatus }): JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex min-w-[84px] items-center justify-center gap-1 rounded-full px-2 py-px",
        "text-[10px] font-medium leading-4 ring-1 ring-inset",
        STATUS_BADGE[status],
      )}
    >
      <span className={cn("size-1 rounded-full", STATUS_DOT[status])} aria-hidden="true" />
      {STATUS_LABEL[status]}
    </span>
  )
}

function ProductCell({ row }: { row: QueueRow }): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <Layers className="size-4 shrink-0 text-text-3" aria-hidden="true" />
        <span className="truncate text-body font-medium text-text">{row.product}</span>
        <span className="shrink-0 rounded-full border border-border-soft bg-surface-sunken px-1.5 font-id text-caption leading-5 text-text-3">
          v{row.versions}
        </span>
      </div>
      <div className="mt-0.5 flex items-center gap-2 font-id text-caption tabular-nums text-text-3">
        <span>EPA {row.epaRegNo}</span>
        <span aria-hidden="true">·</span>
        <span className="truncate font-ui not-italic">{row.submittedBy}</span>
      </div>
    </div>
  )
}

function FindingsCell({ row }: { row: QueueRow }): JSX.Element {
  if (row.findingsOutstanding === 0 && row.findingsCorrected === 0) {
    return <span className="text-body-sm text-text-3">No findings yet</span>
  }
  return (
    <div className="flex items-baseline gap-2 text-body-sm">
      <span className="text-text">
        <span className="font-id tabular-nums font-semibold">{row.findingsOutstanding}</span>{" "}
        outstanding
      </span>
      <span className="text-text-3">·</span>
      <span className="text-text-3">
        <span className="font-id tabular-nums">{row.findingsCorrected}</span> corrected
      </span>
    </div>
  )
}

function CommsCell({ unread }: { unread: number }): JSX.Element {
  if (unread === 0) return <span className="text-body-sm text-text-3">—</span>
  return (
    <span className="inline-flex items-center gap-1.5 text-body-sm text-text">
      <MessageSquare className="size-3.5 text-text-3" aria-hidden="true" />
      <span className="font-id tabular-nums font-semibold">{unread}</span>
      <span className="text-text-3">unread</span>
    </span>
  )
}

function ExpandedThread({ row }: { row: QueueRow }): JSX.Element {
  return (
    <div className="border-t border-border-soft bg-surface-sunken/40 py-3 pl-[60px] pr-6">
      <div className="mb-2 font-id text-caption uppercase tracking-[0.16em] text-text-3">
        Version history · {row.versions} {row.versions === 1 ? "submission" : "submissions"}
      </div>
      <ol className="space-y-0.5">
        {Array.from({ length: row.versions }, (_, idx) => {
          const versionNumber = row.versions - idx
          const isLatest = idx === 0
          return (
            <li key={versionNumber}>
              <VersionRow
                productId={row.id}
                versionNumber={versionNumber}
                isLatest={isLatest}
                supersededBy={isLatest ? null : versionNumber + 1}
              />
            </li>
          )
        })}
      </ol>
    </div>
  )
}

function VersionRow({
  productId,
  versionNumber,
  isLatest,
  supersededBy,
}: {
  productId: string
  versionNumber: number
  isLatest: boolean
  supersededBy: number | null
}): JSX.Element {
  const { toast } = useToast()
  const href = `/reviews/${productId}/v${versionNumber}`
  const label = `Open version ${versionNumber}${isLatest ? " (current)" : ""}`
  // A button rather than a link while the workspace is gated: an anchor still
  // offers a URL to copy, middle-click and bookmark.
  const className = cn(
    "group flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-body-sm",
    "transition-colors duration-120 ease-out hover:bg-brand-tint/60",
    "focus-visible:bg-brand-tint/60 focus-visible:outline focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-[color:var(--brand)]",
  )
  const Wrapper = FEATURES.reviewDetail
    ? ({ children }: { children: React.ReactNode }) => (
        <a href={href} aria-label={label} className={className}>
          {children}
        </a>
      )
    : ({ children }: { children: React.ReactNode }) => (
        <button
          type="button"
          aria-label={label}
          className={className}
          onClick={() => {
            toast(COMING_SOON)
          }}
        >
          {children}
        </button>
      )
  return (
    <Wrapper>
      <span
        className={cn(
          "w-8 shrink-0 font-id text-caption uppercase tracking-[0.1em] tabular-nums",
          isLatest ? "text-brand" : "text-text-3",
        )}
      >
        v{versionNumber}
      </span>
      <span className="min-w-0 flex-1 text-text">
        {isLatest
          ? "Current submission — under review"
          : `Superseded — corrections rolled into v${supersededBy}`}
      </span>
      {isLatest && (
        <span className="shrink-0 rounded-full bg-brand-tint px-1.5 py-0.5 font-id text-micro font-medium text-brand">
          current
        </span>
      )}
      <ChevronRight
        aria-hidden="true"
        className="size-3.5 shrink-0 text-text-3 transition-colors group-hover:text-text"
      />
    </Wrapper>
  )
}

/**
 * State container for the intake wizard. State lives here so the wizard
 * component stays presentational — the actual submit path still needs
 * wiring to `POST /reviews/upload` (see task tracker); this container
 * is where that will land.
 */
function NewReviewContainer({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
}): JSX.Element {
  const [printedFile, setPrintedFile] = React.useState<AcceptedFile | null>(null)
  const [printedStatus, setPrintedStatus] = React.useState<DropzoneStatus>("idle")
  const [regNo, setRegNo] = React.useState("")
  const [printedOn, setPrintedOn] = React.useState<DateStr | null>(null)
  const [labelCode, setLabelCode] = React.useState("")
  const [resolve, setResolve] = React.useState<ResolveState>({ kind: "idle" })
  const [masterFile, setMasterFile] = React.useState<AcceptedFile | null>(null)
  const [masterStatus, setMasterStatus] = React.useState<DropzoneStatus>("idle")
  const [clientId, setClientId] = React.useState<string | null>(null)
  const [submitting, setSubmitting] = React.useState(false)

  const reset = React.useCallback(() => {
    setPrintedFile(null)
    setPrintedStatus("idle")
    setRegNo("")
    setPrintedOn(null)
    setLabelCode("")
    setResolve({ kind: "idle" })
    setMasterFile(null)
    setMasterStatus("idle")
    setClientId(null)
    setSubmitting(false)
  }, [])

  const handleOpenChange = (o: boolean) => {
    if (!o) reset()
    onOpenChange(o)
  }

  return (
    <NewReviewSheet
      open={open}
      onOpenChange={handleOpenChange}
      printedFile={printedFile}
      printedStatus={printedStatus}
      onPrintedSelect={(f) => {
        setPrintedFile({
          name: f.name,
          sizeBytes: f.size,
          kind: f.type === "application/pdf" ? "born_digital" : "scan",
        })
        setPrintedStatus("ready")
      }}
      onPrintedRemove={() => {
        setPrintedFile(null)
        setPrintedStatus("idle")
      }}
      regNo={regNo}
      onRegNoChange={setRegNo}
      printedOn={printedOn}
      onPrintedOnChange={setPrintedOn}
      labelCode={labelCode}
      onLabelCodeChange={setLabelCode}
      resolve={resolve}
      onResolve={() => {
        setResolve({ kind: "resolving" })
        window.setTimeout(() => {
          setResolve({
            kind: "resolved",
            chosen: {
              id: "m-1",
              label: `${regNo || "unknown"} — master`,
              stampedOn: "12 May 2024",
              reason: "In force on the printed date.",
              pages: 8,
              source: "ppls",
            },
            alternatives: [],
          })
        }, 900)
      }}
      onChooseMaster={(id) => {
        if (resolve.kind !== "resolved") return
        const chosen =
          resolve.alternatives.find((m) => m.id === id) ?? resolve.chosen
        setResolve({
          kind: "resolved",
          chosen,
          alternatives: [resolve.chosen, ...resolve.alternatives].filter(
            (m) => m.id !== id,
          ),
        })
      }}
      masterFile={masterFile}
      masterStatus={masterStatus}
      onMasterSelect={(f) => {
        setMasterFile({
          name: f.name,
          sizeBytes: f.size,
          kind: f.type === "application/pdf" ? "born_digital" : "scan",
        })
        setMasterStatus("ready")
      }}
      onMasterRemove={() => {
        setMasterFile(null)
        setMasterStatus("idle")
      }}
      clients={[]}
      clientId={clientId}
      onClientChange={setClientId}
      submitting={submitting}
      onSubmit={() => {
        setSubmitting(true)
        window.setTimeout(() => {
          setSubmitting(false)
          onOpenChange(false)
          reset()
        }, 700)
      }}
    />
  )
}
